/*
  File: ss-welcome-offer.js
  Path: assets/ss-welcome-offer.js

  SanSCARi welcome offer. Works with sections/ss-welcome-offer.liquid.

  What it does:
  1. Shows the offer sheet once, after a delay or a scroll, to people who have not seen it.
  2. On "Claim it": remembers the claim, applies the discount codes to the cart, shows the reward.
  3. Keeps a chip with a live countdown. At the deadline it removes the codes and the chip.
  4. In the cart drawer: makes sure the codes are on the cart, and shows the "one more tee" line
     with its progress bar and what they are saving. The "Pick another" button stays at two tees or more.
  5. In the cart drawer: shows the old price crossed out next to the new one on every discounted
     line, including discounts Shopify applies to the whole order.

  Everything it needs (codes, timings, wording) comes from the section settings.
  What it remembers lives in the shopper's browser, under "ss-wo-v1".
*/
(function () {
  'use strict';

  var root = document.querySelector('[data-ss-wo]');
  var configEl = document.querySelector('[data-ss-wo-config]');
  if (!root || !configEl) return;

  var cfg;
  try {
    cfg = JSON.parse(configEl.textContent);
  } catch (e) {
    return;
  }

  // The theme editor re-runs this file when the section is re-rendered. Clean up the old run first.
  if (window.SSWelcomeOffer && typeof window.SSWelcomeOffer.destroy === 'function') {
    window.SSWelcomeOffer.destroy();
  }

  var KEY = 'ss-wo-v1';
  var APPLIED_KEY = 'ss-wo-applied';
  var DAY = 24 * 60 * 60 * 1000;
  var DRAWER_SECTIONS = ['cart-notification-product', 'cart-notification-button', 'cart-notification-checkout', 'cart-icon-bubble', 'cart-icon-bubble-sticky'];

  var sheet = root.querySelector('[data-ss-wo-sheet]');
  var backdrop = root.querySelector('[data-ss-wo-backdrop]');
  var chip = root.querySelector('[data-ss-wo-chip]');
  var viewOffer = root.querySelector('[data-ss-wo-view="offer"]');
  var viewClaimed = root.querySelector('[data-ss-wo-view="claimed"]');
  var leftEl = root.querySelector('[data-ss-wo-left]');
  var clockEl = root.querySelector('[data-ss-wo-clock]');
  var nudgeTpl = root.querySelector('[data-ss-wo-nudge-tpl]');
  if (!sheet || !chip || !viewOffer || !viewClaimed) return;

  var codes = String(cfg.codes || '').replace(/\s+/g, '');
  var memory = {};
  var timers = [];
  var listeners = [];
  var observers = [];
  var tickTimer = null;
  var lastFocus = null;
  var syncing = false;
  var syncQueued = false;
  var destroyed = false;

  /* ---------- small helpers ---------- */

  function readState() {
    try {
      var raw = window.localStorage.getItem(KEY);
      return raw ? JSON.parse(raw) || {} : {};
    } catch (e) {
      return memory;
    }
  }

  function writeState(state) {
    memory = state;
    try {
      window.localStorage.setItem(KEY, JSON.stringify(state));
    } catch (e) {}
  }

  function sessionGet(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function sessionSet(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (e) {}
  }

  function on(target, type, handler, options) {
    target.addEventListener(type, handler, options);
    listeners.push([target, type, handler, options]);
  }

  function later(fn, ms) {
    var id = window.setTimeout(fn, ms);
    timers.push(id);
    return id;
  }

  function announce(action) {
    try {
      document.dispatchEvent(new CustomEvent('ss:welcome-offer', { detail: { action: action } }));
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish('ss_welcome_offer', { action: action });
      }
    } catch (e) {}
  }

  function isActive() {
    var state = readState();
    return state.status === 'claimed' && state.until > Date.now();
  }

  // The offer runs to midnight. Claimed late in the evening, it runs to the midnight after.
  function deadlineFrom(now) {
    var d = new Date(now);
    var late = d.getHours() >= Number(cfg.lateHour || 21);
    d.setHours(24, 0, 0, 0);
    if (late) d.setDate(d.getDate() + 1);
    return d.getTime();
  }

  function pad(n) {
    return n < 10 ? '0' + n : String(n);
  }

  function formatMoney(cents) {
    var format = cfg.moneyFormat || '${{amount}}';
    var amount = (cents / 100).toFixed(2);
    var whole = String(Math.round(cents / 100));
    var withCommas = function (value, thousands, decimal) {
      var parts = value.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.join(decimal);
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, function (match, name) {
      switch (name) {
        case 'amount':
          return withCommas(amount, ',', '.');
        case 'amount_no_decimals':
          return withCommas(whole, ',', '.');
        case 'amount_with_comma_separator':
          return withCommas(amount, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withCommas(whole, '.', ',');
        default:
          return withCommas(amount, ',', '.');
      }
    });
  }

  /* ---------- cart ---------- */

  function getCart() {
    return fetch(cfg.cartUrl + '.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(function (response) {
      if (!response.ok) throw new Error('cart ' + response.status);
      return response.json();
    });
  }

  function setDiscount(value, withSections) {
    var body = { discount: value };
    if (withSections) {
      body.sections = DRAWER_SECTIONS;
      body.sections_url = window.location.pathname;
    }
    return fetch(cfg.cartUpdateUrl + '.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (response) {
      if (!response.ok) throw new Error('cart update ' + response.status);
      return response.json();
    });
  }

  // True when every one of our codes is already on this cart.
  function cartHasCodes(cart) {
    if (!codes) return true;
    if (Array.isArray(cart.discount_codes)) {
      var onCart = cart.discount_codes.map(function (entry) {
        return String((entry && entry.code) || entry || '').toUpperCase();
      });
      return codes.toUpperCase().split(',').every(function (code) {
        return onCart.indexOf(code) !== -1;
      });
    }
    // Older cart responses do not list codes. Fall back to what we did in this tab.
    return sessionGet(APPLIED_KEY) === String(cart.token || '');
  }

  function rememberApplied(cart) {
    if (cart && cart.token) sessionSet(APPLIED_KEY, String(cart.token));
  }

  function applyCodes() {
    if (!codes || cfg.designMode) return Promise.resolve(null);
    return setDiscount(codes, false)
      .then(function (cart) {
        rememberApplied(cart);
        return cart;
      })
      .catch(function () {
        return null;
      });
  }

  // Swap the drawer's contents for fresh ones, the same way the theme does it.
  function renderDrawerSections(sections) {
    if (!sections) return;
    DRAWER_SECTIONS.forEach(function (id) {
      var container = document.getElementById(id);
      var html = sections[id];
      if (!container || !html) return;
      var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
      if (fresh) container.innerHTML = fresh.innerHTML;
    });
  }

  /* ---------- cart drawer line ---------- */

  function renderNudge(cart) {
    var product = document.getElementById('cart-notification-product');
    if (!product || !product.parentNode || !nudgeTpl || !cart) return;

    var nudge = product.parentNode.querySelector('[data-ss-wo-nudge]');
    if (!nudge) {
      var fragment = nudgeTpl.content ? nudgeTpl.content.cloneNode(true) : null;
      nudge = fragment ? fragment.querySelector('[data-ss-wo-nudge]') : null;
      if (!nudge) return;
      product.parentNode.insertBefore(nudge, product);
    }

    var text = nudge.querySelector('[data-ss-wo-nudge-text]');
    var tagIcon = nudge.querySelector('[data-ss-wo-nudge-tag]');
    var checkIcon = nudge.querySelector('[data-ss-wo-nudge-check]');
    var bar = nudge.querySelector('[data-ss-wo-nudge-bar]');
    var save = nudge.querySelector('[data-ss-wo-nudge-save]');
    var count = Number(cart.item_count || 0);
    var discount = Number(cart.total_discount || 0);
    var html = '';
    var saveHtml = '';
    var paired = false;

    if (count === 1) {
      html = isActive() ? cfg.nudgeOneClaimed : cfg.nudgeOne;
      // One tee with the welcome offer on it: say what they are already saving.
      if (isActive() && discount > 0 && cfg.nudgeSaving) {
        saveHtml = String(cfg.nudgeSaving).replace('[amount]', formatMoney(discount));
      }
    } else if (count >= 2 && discount > 0) {
      html = String(cfg.nudgePair || '').replace('[amount]', formatMoney(discount));
      paired = true;
    }

    if (!html) {
      nudge.hidden = true;
      return;
    }
    if (text.innerHTML !== html) text.innerHTML = html;
    if (tagIcon) tagIcon.hidden = paired;
    if (checkIcon) checkIcon.hidden = !paired;
    if (bar) {
      // Two steps: the first fills at one tee, the second at two.
      Array.prototype.forEach.call(bar.children, function (step, index) {
        step.classList.toggle('is-on', index < count);
      });
      bar.hidden = false;
    }
    if (save) {
      if (save.innerHTML !== saveHtml) save.innerHTML = saveHtml;
      save.hidden = !saveHtml;
    }
    nudge.hidden = false;
  }

  /* ---------- crossed-out prices in the cart drawer ---------- */

  // What each line costs after every discount. Shopify takes "amount off order" discounts off the
  // cart total, not off each line, so that part is shared out here in proportion to each line.
  function linePrices(cart) {
    var items = cart.items || [];
    var subtotal = 0;
    items.forEach(function (item) {
      subtotal += Number(item.final_line_price || 0);
    });
    var orderLevel = Math.max(0, subtotal - Number(cart.total_price || 0));
    var left = orderLevel;

    return items.map(function (item, index) {
      var line = Number(item.final_line_price || 0);
      var share = 0;
      if (orderLevel > 0 && subtotal > 0) {
        // The last line takes whatever is left, so the lines always add up to the cart total.
        share = index === items.length - 1 ? left : Math.round((orderLevel * line) / subtotal);
        share = Math.max(0, Math.min(share, left, line));
        left -= share;
      }
      return {
        was: Number(item.original_line_price || 0),
        now: line - share,
        quantity: Math.max(1, Number(item.quantity || 1)),
        share: share
      };
    });
  }

  function ownPriceNode(holder) {
    for (var i = 0; i < holder.children.length; i++) {
      if (holder.children[i].hasAttribute('data-ss-wo-price')) return holder.children[i];
    }
    return null;
  }

  function hiddenLabel(label) {
    var el = document.createElement('span');
    el.className = 'visually-hidden';
    el.textContent = label;
    return el;
  }

  // Puts "old price crossed out, new price" in one of the theme's price spots, using the theme's own classes.
  function paintPrice(holder, show, kind, was, now) {
    var own = ownPriceNode(holder);
    if (!show) {
      if (own) holder.removeChild(own);
      holder.classList.remove('ss-wo-priced');
      return;
    }
    var signature = was + '|' + now;
    if (own && own.getAttribute('data-ss-wo-price') === signature) return;

    var node = document.createElement(kind === 'line' ? 'div' : 'span');
    node.setAttribute('data-ss-wo-price', signature);
    var oldPrice = document.createElement('s');
    var newPrice = document.createElement('span');
    oldPrice.textContent = was;
    newPrice.textContent = now;
    if (kind === 'line') {
      node.className = 'cart-notification-item__discounted-prices';
      oldPrice.className = 'cart-notification-item__old-price price price--end';
      newPrice.className = 'price price--end';
    } else {
      oldPrice.className = 'price-item price-item--regular';
      newPrice.className = 'price-item price-item--sale';
    }
    if (cfg.regularLabel) node.appendChild(hiddenLabel(cfg.regularLabel));
    node.appendChild(oldPrice);
    node.appendChild(document.createTextNode(' '));
    if (cfg.saleLabel) node.appendChild(hiddenLabel(cfg.saleLabel));
    node.appendChild(newPrice);

    if (own) holder.replaceChild(node, own);
    else holder.insertBefore(node, holder.firstChild);
    holder.classList.add('ss-wo-priced');
  }

  function renderPrices(cart) {
    var product = document.getElementById('cart-notification-product');
    if (!product || !cart) return;

    linePrices(cart).forEach(function (price, index) {
      var row = document.getElementById('CartItem-' + (index + 1));
      if (!row || !product.contains(row)) return;
      // Lines the theme already crosses out by itself are left alone. Only step in for the order-level part.
      var show = price.share > 0 && price.now < price.was;
      var wasEach = formatMoney(Math.round(price.was / price.quantity));
      var nowEach = formatMoney(Math.round(price.now / price.quantity));
      var wasLine = formatMoney(price.was);
      var nowLine = formatMoney(price.now);

      Array.prototype.forEach.call(row.querySelectorAll('.cart-notification-item__price'), function (holder) {
        paintPrice(holder, show, 'each', wasEach, nowEach);
      });
      Array.prototype.forEach.call(row.querySelectorAll('.cart-notification-item__price-wrapper'), function (holder) {
        paintPrice(holder, show, 'line', wasLine, nowLine);
      });
    });

    // These were our own edits. Clear them from the watchers so they do not trigger another round.
    observers.forEach(function (observer) {
      observer.takeRecords();
    });
  }

  // Runs when the drawer opens or its contents change.
  function syncCart() {
    if (destroyed || cfg.designMode) return;
    if (syncing) {
      syncQueued = true;
      return;
    }
    syncing = true;
    getCart()
      .then(function (cart) {
        if (isActive() && cart.item_count > 0 && !cartHasCodes(cart)) {
          return setDiscount(codes, true).then(function (updated) {
            rememberApplied(updated);
            renderDrawerSections(updated.sections);
            return updated;
          });
        }
        return cart;
      })
      .then(function (cart) {
        renderNudge(cart);
        renderPrices(cart);
      })
      .catch(function () {})
      .then(function () {
        syncing = false;
        if (syncQueued) {
          syncQueued = false;
          later(syncCart, 150);
        }
      });
  }

  function watchDrawer() {
    var drawer = document.getElementById('cart-notification');
    var product = document.getElementById('cart-notification-product');
    if (!drawer || !('MutationObserver' in window)) return;

    var debounce = null;
    var queue = function () {
      window.clearTimeout(debounce);
      debounce = later(function () {
        if (drawer.classList.contains('active')) syncCart();
      }, 120);
    };

    var classObserver = new MutationObserver(queue);
    classObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
    observers.push(classObserver);

    if (product) {
      var contentObserver = new MutationObserver(queue);
      contentObserver.observe(product, { childList: true, subtree: true });
      observers.push(contentObserver);
    }
  }

  /* ---------- sheet ---------- */

  function showView(name) {
    viewOffer.hidden = name !== 'offer';
    viewClaimed.hidden = name !== 'claimed';
    sheet.classList.toggle('is-claimed', name === 'claimed');
  }

  function openSheet(name) {
    showView(name);
    lastFocus = document.activeElement;
    chip.hidden = true;
    sheet.hidden = false;
    if (backdrop) backdrop.hidden = false;
    // Let the browser register the starting position before animating in.
    void sheet.offsetHeight;
    sheet.classList.add('is-open');
    if (backdrop) backdrop.classList.add('is-open');
    try {
      sheet.focus({ preventScroll: true });
    } catch (e) {
      sheet.focus();
    }
  }

  function closeSheet() {
    if (sheet.hidden) return;
    sheet.classList.remove('is-open');
    if (backdrop) backdrop.classList.remove('is-open');
    later(function () {
      sheet.hidden = true;
      if (backdrop) backdrop.hidden = true;
      if (isActive() || (cfg.designMode && sheet.classList.contains('is-claimed'))) chip.hidden = false;
    }, 300);
    if (lastFocus && typeof lastFocus.focus === 'function' && document.contains(lastFocus)) {
      try {
        lastFocus.focus({ preventScroll: true });
      } catch (e) {}
    }
  }

  function dismiss() {
    if (!cfg.designMode && !isActive()) {
      writeState({ status: 'dismissed', next: Date.now() + Number(cfg.dismissDays || 3) * DAY });
      announce('dismissed');
    }
    closeSheet();
  }

  function claim() {
    var now = Date.now();
    var until = deadlineFrom(now);
    if (!cfg.designMode) {
      writeState({ status: 'claimed', at: now, until: until });
      applyCodes();
      announce('claimed');
    } else {
      memory = { status: 'claimed', at: now, until: until };
    }
    showView('claimed');
    startCountdown(until);
  }

  /* ---------- countdown ---------- */

  function paintCountdown(until) {
    var left = Math.max(0, until - Date.now());
    var totalSeconds = Math.floor(left / 1000);
    var hours = Math.floor(totalSeconds / 3600);
    var minutes = Math.floor((totalSeconds % 3600) / 60);
    var seconds = totalSeconds % 60;
    if (clockEl) clockEl.textContent = hours + ':' + pad(minutes) + ':' + pad(seconds);
    if (leftEl) {
      var short = hours > 0 ? hours + 'h ' + minutes + 'm' : minutes + 'm ' + pad(seconds) + 's';
      leftEl.textContent = '· ' + short + (cfg.leftText ? ' ' + cfg.leftText : '');
    }
    return left;
  }

  function startCountdown(until) {
    window.clearInterval(tickTimer);
    paintCountdown(until);
    tickTimer = window.setInterval(function () {
      if (paintCountdown(until) <= 0) expire();
    }, 1000);
  }

  function expire() {
    window.clearInterval(tickTimer);
    tickTimer = null;
    chip.hidden = true;
    closeSheet();
    if (cfg.designMode) return;
    writeState({ status: 'expired', next: Date.now() + Number(cfg.cooldownDays || 30) * DAY });
    announce('expired');
    if (codes) {
      setDiscount('', false)
        .then(function () {
          sessionSet(APPLIED_KEY, '');
          syncCart();
        })
        .catch(function () {});
    }
  }

  /* ---------- when to show the offer ---------- */

  function mayOffer() {
    if (cfg.returning || cfg.template === 'cart') return false;
    var state = readState();
    if (state.status === 'claimed') return false;
    if (state.next && state.next > Date.now()) return false;
    return true;
  }

  // Do not interrupt: an open drawer, menu, size picker or a field being typed in.
  function busy() {
    if (document.body.classList.contains('overflow-hidden')) return true;
    var drawer = document.getElementById('cart-notification');
    if (drawer && drawer.classList.contains('active')) return true;
    if (document.querySelector('[data-ss-atc-sizes]:not([hidden])')) return true;
    var el = document.activeElement;
    return !!(el && /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName));
  }

  function scheduleOffer() {
    var shown = false;
    var show = function () {
      if (shown || destroyed || !mayOffer()) return;
      if (busy()) {
        later(show, 3000);
        return;
      }
      shown = true;
      openSheet('offer');
      announce('shown');
    };

    later(show, Math.max(1, Number(cfg.delay || 12)) * 1000);

    var threshold = Number(cfg.scroll || 0);
    if (threshold > 0) {
      var onScroll = function () {
        var doc = document.documentElement;
        var room = doc.scrollHeight - window.innerHeight;
        if (room > 200 && (window.pageYOffset / room) * 100 >= threshold) show();
      };
      on(window, 'scroll', onScroll, { passive: true });
    }
  }

  /* ---------- start ---------- */

  on(root, 'click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;
    if (target.closest('[data-ss-wo-claim]')) return claim();
    if (target.closest('[data-ss-wo-dismiss]') || target.closest('[data-ss-wo-backdrop]')) return dismiss();
    if (target.closest('[data-ss-wo-x]')) return viewClaimed.hidden ? dismiss() : closeSheet();
    if (target.closest('[data-ss-wo-close]')) return closeSheet();
    if (target.closest('[data-ss-wo-chip]')) return openSheet('claimed');
  });

  on(document, 'keydown', function (event) {
    if (event.key !== 'Escape' || sheet.hidden) return;
    if (viewClaimed.hidden) dismiss();
    else closeSheet();
  });

  if (cfg.designMode) {
    // In the theme editor: show the sheet while this section is selected, so the wording can be edited.
    on(document, 'shopify:section:select', function (event) {
      if (event.detail && event.detail.sectionId === cfg.sectionId) openSheet('offer');
    });
    on(document, 'shopify:section:deselect', function (event) {
      if (event.detail && event.detail.sectionId === cfg.sectionId) {
        showView('offer');
        closeSheet();
        chip.hidden = true;
      }
    });
  } else {
    var state = readState();
    if (state.status === 'claimed') {
      if (state.until > Date.now()) {
        chip.hidden = false;
        startCountdown(state.until);
        // A new tab or a fresh cart may not carry the codes yet.
        if (sessionGet(APPLIED_KEY) === null) applyCodes();
      } else {
        expire();
      }
    } else if (mayOffer()) {
      scheduleOffer();
    }
    watchDrawer();
  }

  window.SSWelcomeOffer = {
    destroy: function () {
      destroyed = true;
      window.clearInterval(tickTimer);
      timers.forEach(function (id) {
        window.clearTimeout(id);
      });
      listeners.forEach(function (entry) {
        entry[0].removeEventListener(entry[1], entry[2], entry[3]);
      });
      observers.forEach(function (observer) {
        observer.disconnect();
      });
    },
    // For testing from the browser console: SSWelcomeOffer.reset() forgets everything on this browser.
    reset: function () {
      try {
        window.localStorage.removeItem(KEY);
        window.sessionStorage.removeItem(APPLIED_KEY);
      } catch (e) {}
      memory = {};
    }
  };
})();